import { IconButton, Table, TableBody, TableCell, TableContainer, TableRow, Typography, Box } from '@mui/material';
import { t } from 'i18next';
import React, { useEffect, useState } from 'react';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import { getAllAutojobs } from 'src/shared/services/autojobService';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import { formatDateAndTime } from 'src/shared/functions/formatDate';
import { AccessTimeFilled, Settings } from '@mui/icons-material';
import { AutojobResponse } from '../../models/autojobResponse';
import { UpcomingAutojobModal } from './upcomingJobsModal';

type AllUpcomingAutojobsModalProps = {
  modalOpen: boolean;
  onClose: () => void;
};

export const AllUpcomingAutojobsModal = (props: AllUpcomingAutojobsModalProps) => {
  const { modalOpen, onClose } = props;
  const isMounted = useIsMounted();

  const [loading, setLoading] = useState<boolean>(false);
  const [upcomingAutojobs, setUpcomingAutojobs] = useState<AutojobResponse[]>([]);
  const [selectedAutojob, setSelectedAutojob] = useState<AutojobResponse | undefined>(undefined);
  const [openUpcomingJobModal, setOpenUpcomingJobModal] = useState<boolean>(false);

  useEffect(() => {
    const fetchUpcomingData = async () => {
      setLoading(true);
      try {
        const result = await getAllAutojobs({ pageSize: 25, status: 1 });
        if (isMounted()) {
          setUpcomingAutojobs(result.data.results);
        }
      } catch (e) {
        if (isMounted()) {
          ConsoleHelper.error(e);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchUpcomingData();
  }, [isMounted]);

  const onHandleClose = React.useCallback(() => {
    onClose();
  }, [onClose]);

  const TextItem = ({ text }) => (
    <Typography variant='body2' color={(thm) => thm.palette.text.primary}>
      {text}
    </Typography>
  );

  return (
    <GenericDialog
      open={modalOpen}
      dialogTitle={t('dashboard:autojobs.upcomingJobs')}
      dialogContent={
        <Box sx={{ p: 1 }}>
          <LoadingOverlay loading={loading} />
          {upcomingAutojobs && upcomingAutojobs.length > 0 ? (
            <TableContainer>
              <Table>
                <TableBody>
                  {upcomingAutojobs.map((item) => {
                    return (
                      <TableRow key={item.task_id}>
                        <TableCell>
                          <AccessTimeFilled fontSize='small' color='disabled' />
                        </TableCell>
                        <TableCell>
                          <TextItem text={formatDateAndTime(item.startTimeStamp)} />
                        </TableCell>
                        <TableCell>
                          <TextItem text={item.taskName} />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size='small'
                            onClick={() => {
                              setSelectedAutojob(item);
                              setOpenUpcomingJobModal(true);
                            }}
                          >
                            <Settings fontSize='small' color='primary' />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography sx={{ color: 'grey.500', py: 1 }}>{t('dashboard:autojobs.chart.noUpcomingJobs')}</Typography>
          )}
          {selectedAutojob && (
            <UpcomingAutojobModal
              modalOpen={openUpcomingJobModal}
              onClose={() => {
                setSelectedAutojob(undefined);
                setOpenUpcomingJobModal(false);
              }}
              taskId={selectedAutojob.task_id}
            />
          )}
        </Box>
      }
      onClose={onHandleClose}
      onSave={onHandleClose}
      maxWidth='sm'
      showDialogActions={false}
    />
  );
};
